// Gemeinsame Consent-Mode Logik für Web und App
// Siehe Fehler: https://thaliamayersche.atlassian.net/browse/WDD-1491
// https://docs.usercentrics.com/#/consent-mode?id=step-2-optional-use-the-usercentrics-cmp-events-to-signal-the-consent-status-via-the-consent-mode-api-for-custom-data-processing-services
import { sendData } from "../../assets/js/datalayer.js";
import CONSENT_UPDATE_EVENT_NAME from "./como-update-event-name.js";

/**
 * Konvertiert einen Wert zu einem Boolean
 * Behandelt String-Werte "true" und "false" korrekt
 * @param {boolean|string|null|undefined} value - Der zu konvertierende Wert
 * @returns {boolean|null} Der konvertierte Boolean-Wert oder null
 */
function toBoolean(value) {
	if (value === null || value === undefined) {
		return null;
	}
	if (typeof value === 'string') {
		return value.toLowerCase() === 'true';
	}
	return Boolean(value);
}

/**
 * Verarbeitet Consent-Updates und triggert entsprechende gtag-Calls
 * @param {Object} options - Consent-Optionen
 * @param {boolean|string|null} options.adsGranted - Consent-Status für Ads (auch als String "true"/"false" erlaubt)
 * @param {boolean|string|null} options.analyticsGranted - Consent-Status für Analytics (auch als String "true"/"false" erlaubt)
 * @param {boolean|string} options.adsExists - Ob Ads-Service existiert (auch als String "true"/"false" erlaubt)
 * @param {boolean|string} options.analyticsExists - Ob Analytics-Service existiert (auch als String "true"/"false" erlaubt)
 * @param {string} options.eventType - Event-Typ (implicit/explicit)
 */
export function processConsentUpdate({
	adsGranted,
	analyticsGranted,
	adsExists,
	analyticsExists,
	eventType
}) {
	// Normalisiere String-Werte zu Booleans
	const normalizedAdsGranted = toBoolean(adsGranted);
	const normalizedAnalyticsGranted = toBoolean(analyticsGranted);
	const normalizedAdsExists = toBoolean(adsExists);
	const normalizedAnalyticsExists = toBoolean(analyticsExists);

	if (normalizedAdsExists && normalizedAnalyticsExists) {
		updateAll(normalizedAdsGranted, normalizedAnalyticsGranted);
	} else if (normalizedAdsExists) {
		updateAds(normalizedAdsGranted);
	} else if (normalizedAnalyticsExists) {
		updateAnalytics(normalizedAnalyticsGranted);
	}

	// WDD-1493
	// Wir pushen ein zusätzliches Event in den Datalayer (como_update),
	// das für GA4 Events und GTM Tags als Trigger dient.
	// Erklärung Usercentrics consent_status "type"
	// implicit: Standard Status der Usercentrics DPS (Data Processing Service, i.d.R. alle false)
	// explicit: vom Nutzer gesetzter Status eines (DPS)
	// HINWEIS/WICHTIG: Muss nach dem eigentlichen Consent Signal erfolgen, um sicherzustellen, dass die Consent Daten im GTM vorliegen
	if (eventType.toLowerCase() === "explicit") {
		sendComoUpdateEvent(normalizedAdsGranted, normalizedAnalyticsGranted);
	}
}

export function updateAll(ucAdServiceGranted, ucAnalyticsServiceGranted) {
	setConsent("update", {
		ad_storage: ucAdServiceGranted ? "granted" : "denied",
		ad_user_data: ucAdServiceGranted ? "granted" : "denied",
		ad_personalization: ucAdServiceGranted ? "granted" : "denied",
		analytics_storage: ucAnalyticsServiceGranted ? "granted" : "denied"
	});
}

export function updateAnalytics(ucAnalyticsServiceGranted) {
	setConsent("update", {
		analytics_storage: ucAnalyticsServiceGranted ? "granted" : "denied"
	});
}

export function updateAds(ucAdServiceGranted) {
	setConsent("update", {
		ad_storage: ucAdServiceGranted ? "granted" : "denied",
		ad_user_data: ucAdServiceGranted ? "granted" : "denied",
		ad_personalization: ucAdServiceGranted ? "granted" : "denied"
	});
}

export function sendComoUpdateEvent(ucAdServiceGranted, ucAnalyticsServiceGranted) {
	const consentmodeData = {
		ad_storage: ucAdServiceGranted ? "granted" : "denied",
		ad_user_data: ucAdServiceGranted ? "granted" : "denied",
		ad_personalization: ucAdServiceGranted ? "granted" : "denied",
		analytics_storage: ucAnalyticsServiceGranted ? "granted" : "denied"
	}
	// Datalayer Event
	sendData({
		event: CONSENT_UPDATE_EVENT_NAME,
		services: consentmodeData,
	});
	// Custom Event
	sendCustomEvent(CONSENT_UPDATE_EVENT_NAME, consentmodeData);
}

export function sendCustomEvent(event, consentmodeData) {
	const customEvent = new CustomEvent(event, {
		bubbles: true,
		detail: {
			services: consentmodeData
		}
	});
	document.dispatchEvent(customEvent);
}

function setConsent(consentType, consentParams) {
	// gtag schreibt in den gtagDataLayer
	window.gtag('consent', consentType, consentParams);

	// Zusätzlich dataLayer (gtagGtm) und newDataLayer (gtagGtmLegacy) befüllen,
	// damit GTM Preview die Consent Signale auf beiden Datalayern erkennt.
	window.gtagGtm('consent', consentType, consentParams);
	window.gtagGtmLegacy('consent', consentType, consentParams);
}
